import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaLinkedinIn, FaTwitter, FaEnvelope } from 'react-icons/fa';
import { useApiData } from '../hooks/useApiData';
import { fetchTeamSection, fetchImage } from '../repository/api';
import martin from '../assets/martin.webp';
import Eunice from '../assets/Eunice.webp';
import Janice from '../assets/Janice.webp';
import Alvin from '../assets/Alvin.webp';
import Faridah from '../assets/Faridah.webp'
import janiceexecutivemarketer from '../assets/janice-executive-marketer.png';
import Headofmarketing from '../assets/Head-of-Marketing.jpg';
import Jacktoneombuy from '../assets/Jactone-Ast.-digital-marketer.jpg';
import christine from '../assets/christine-mobipesa.jpg'
import Samuel from '../assets/Evans.png';

const Team = ({ maxMembers = null }) => {
  // Fetch team data from API
  const { data: teamData, loading, error } = useApiData(fetchTeamSection);

  // Default fallback data
  const defaultTeamMembers = [
    {
      id: 'member-1',
      name: 'Martin Waititu',
      title: 'Head of Operations',
      image: martin,
      bio: 'Martin brings over 20 years of experience in telecom and software development. His vision drives our growth and innovation strategy.',
      color: '#2090B0',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        email: 'mailto:sarah@codewave.co.ke'
      }
    },
    {
      id: 'member-2',
      name: 'Samuel Gakere',
      title: 'Chief Technology Officer',
      image: Samuel,
      bio: 'Samuel has over 10 years of experience in software development. He is a seasoned software engineer with a passion for building scalable and efficient systems.',
      color: '#105090',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        email: 'mailto:david@codewave.co.ke'
      }
    },
    {
      id: 'member-3',
      name: 'Janice Anyula',
      title: 'Head of Customer Relations',
      image: Janice,
      bio: 'Janice has over 10 years of experience in customer relation. She is a seasoned customer relation officer with a passion for building strong relationships with clients.',
      color: '#2090B0',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        email: 'mailto:amina@codewave.co.ke'
      }
    },
    {
      id: 'member-4',
      name: 'Laura Gathura',
      title: 'Head of Marketing',
      image: Headofmarketing,
      bio: 'Laura has over 10 years of experience in marketing. She is a seasoned marketing manager with a passion for building products that solve real business challenges.',
      color: '#105090',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        email: 'mailto:michael@codewave.co.ke'
      }
    },
    {
      id: 'member-5',
      name: 'Jacktone Ombuya',
      title: 'Marketing Executive',
      image: Jacktoneombuy,
      bio: 'Develop and implement strategic plans to promote and sell products or services, utilizing various channels to reach target audiences and achieve business goals.',
      color: '#2090B0',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        email: 'mailto:grace@codewave.co.ke'
      }
    },
    {
      id: 'member-6',
      name: 'Christine Khang"atsi',
      title: 'Head of Business Development',
      image: christine,
      bio: 'Develop and maintain client relationship. Develop sales and preparing quotes. ',
      color: '#105090',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        email: 'mailto:daniel@codewave.co.ke'
      }
    }
  ];

  // Transform API data to match component structure
  const transformApiData = (apiData) => {
    if (!apiData || !apiData.team_members) return defaultTeamMembers;

    const colorMap = ['#2090B0', '#105090', '#2090B0', '#105090', '#2090B0', '#2090B0', '#105090', '#2090B0'];

    return apiData.team_members.map((member, index) => ({
      id: `member-${index + 1}`,
      name: member.name,
      title: member.role,
      image: member.photo_path ? fetchImage(member.photo_path) : defaultTeamMembers[index]?.image || martin,
      bio: member.experience_summary,
      color: colorMap[index] || '#2090B0',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        email: `mailto:${member.name.toLowerCase().replace(/\s+/g, '.')}@codewave.co.ke`
      }
    }));
  };

  // Use API data or fallback to default
  const allTeamMembers = teamData ? transformApiData(teamData) : defaultTeamMembers;

  // Apply member limit if specified
  const teamMembers = maxMembers ? allTeamMembers.slice(0, maxMembers) : allTeamMembers;
  const firstHeading = teamData?.first_heading || 'Meet Our Team';
  const firstParagraph = teamData?.first_paragraph || 'The passionate minds behind Codewave\'s innovation and success';
  const secondHeading = teamData?.second_heading || 'Our Commitment to Excellence';
  const secondParagraph = teamData?.second_paragraph || 'We are a passionate group of professionals dedicated to delivering exceptional results and driving innovation in everything we do.';

  // Background shape data
  const shapes = [
    { className: "circle", color: "#105090", top: "15%", right: "5%", size: "180px", id: "team-shape-1" },
    { className: "triangle", color: "#2090B0", bottom: "20%", left: "8%", size: "120px", id: "team-shape-2" },
    { className: "hexagon", color: "#105090", top: "50%", right: "12%", size: "90px", id: "team-shape-3" },
    { className: "square", color: "#2090B0", top: "20%", left: "15%", size: "70px", id: "team-shape-4" }
  ];

  // Show loading state while fetching data
  if (loading) {
    return (
      <TeamSection id="team">
        <Container>
          <SectionHeader>
            <SectionTitle>Meet Our <HighlightText>Team</HighlightText></SectionTitle>
            <SectionDescription>
              Loading team information...
            </SectionDescription>
          </SectionHeader>
        </Container>
      </TeamSection>
    );
  }

  // Show error state if API call failed
  if (error) {
    console.error('Team section error:', error);
    // Continue with default data instead of showing error
  }

  return (
    <TeamSection id="team">
      <BackgroundShapes>
        {shapes.map((shape, index) => (
          <GeometricShape
            key={shape.id}
            className={shape.className}
            color={shape.color}
            top={shape.top}
            left={shape.left}
            right={shape.right}
            bottom={shape.bottom}
            size={shape.size}
          />
        ))}
      </BackgroundShapes>

      <Container>
        <SectionHeader>
          <SectionTitle>
            {firstHeading.includes('Team') ? (
              <>
                {firstHeading.replace('Team', '').trim()} <HighlightText>Team</HighlightText>
              </>
            ) : (
              firstHeading
            )}
          </SectionTitle>
          <SectionDescription>
            {firstParagraph}
          </SectionDescription>
        </SectionHeader>

        <TeamIntro>
          <IntroContent>
            <IntroHeading>
              {secondHeading.includes('Excellence') ? (
                <>
                  {secondHeading.replace('Excellence', '').trim()} <HighlightText>Excellence</HighlightText>
                </>
              ) : (
                secondHeading
              )}
            </IntroHeading>

            <IntroParagraph>
              {secondParagraph}
            </IntroParagraph>

            <IntroParagraph>
              Each member of our team contributes unique skills and perspectives, creating a collaborative environment
              where innovation thrives. We're committed to delivering solutions that exceed expectations and drive
              meaningful impact for the businesses we serve.
            </IntroParagraph>
          </IntroContent>
        </TeamIntro>

        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamMemberCard
              key={member.id}
              delay={index * 0.05}
              color={member.color}
            >
              <MemberImageContainer>
                <MemberImage src={member.image} alt={member.name} />
                <ImageOverlay color={member.color}>
                  <SocialLinks>
                    {Object.entries(member.social).map(([platform, url]) => (
                      <SocialLink
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${member.name}'s ${platform}`}
                      >
                        {platform === 'linkedin' && <FaLinkedinIn />}
                        {platform === 'twitter' && <FaTwitter />}
                        {platform === 'email' && <FaEnvelope />}
                      </SocialLink>
                    ))}
                  </SocialLinks>
                </ImageOverlay>
              </MemberImageContainer>

              <MemberDetails color={member.color}>
                <MemberName>{member.name}</MemberName>
                <MemberTitle>{member.title}</MemberTitle>
                <MemberBio>{member.bio}</MemberBio>
              </MemberDetails>
            </TeamMemberCard>
          ))}
        </TeamGrid>

        <JoinTeam
          id="join-team"
        >
          <JoinTeamContent>
            <JoinTeamTitle>Join Our <HighlightText>Team</HighlightText></JoinTeamTitle>
            <JoinTeamDescription>
              We're always looking for talented individuals who are passionate about technology and innovation.
              Check out our current openings or send us your resume!
            </JoinTeamDescription>
            <JoinTeamButton>View Career Opportunities</JoinTeamButton>
          </JoinTeamContent>
        </JoinTeam>
      </Container>
    </TeamSection>
  );
};

export default Team;

// === Animations ===
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const floatAnimation = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(5px, -5px) rotate(2deg); }
  50% { transform: translate(0, 0) rotate(0deg); }
  75% { transform: translate(-5px, 5px) rotate(-2deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

// === Styled Components ===
const TeamSection = styled.section`
  padding: 120px 40px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fb 0%,rgb(162, 165, 171) 100%);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  
  @media (max-width: 992px) {
    padding: 80px 30px;
  }
  
  @media (max-width: 576px) {
    padding: 60px 20px;
  }
`;

const BackgroundShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const GeometricShape = styled.div`
  position: absolute;
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  width: ${props => props.size};
  height: ${props => props.size};
  background-color: ${props => props.color};
  opacity: 0.05;
  z-index: 0;
  animation: ${floatAnimation} ${props => 10 + Math.random() * 10}s infinite ease-in-out;
  
  &.circle {
    border-radius: 50%;
  }
  
  &.square {
    transform: rotate(45deg);
  }
  
  &.triangle {
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  }
  
  &.hexagon {
    clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  width: 100%;
  animation: ${fadeIn} 0.4s ease-out;
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  z-index: 2;
  color: #105090;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: ${gradientAnimation} 4s ease infinite;
`;

const SectionDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  color: #666;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Team intro section styling
const TeamIntro = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 900px;
  margin: 0 0 60px;
  animation: ${fadeIn} 0.4s ease-out;
  
  @media (max-width: 768px) {
    margin: 0 0 40px;
  }
`;

const IntroContent = styled.div`
  text-align: center;
`;

const IntroHeading = styled.h3`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 25px;
  position: relative;
  line-height: 1.4;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #105090, #2090B0);
    background-size: 200% auto;
    animation: ${gradientAnimation} 4s ease infinite;
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
  
  @media (max-width: 576px) {
    font-size: 20px;
  }
`;

const IntroParagraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #444;
  margin-bottom: 20px;
  
  @media (max-width: 576px) {
    font-size: 15px;
    line-height: 1.7;
  }
`;

// Team grid styling
const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }
`;

const TeamMemberCard = styled.div`
  background: #FFFFFF;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  animation: ${fadeIn} 0.3s ease ${props => props.delay}s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const MemberImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  height: 300px;
  
  @media (max-width: 768px) {
    height: 250px;
  }
`;

const MemberImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  transition: transform 0.5s ease;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, ${props => props.color}CC, transparent);
  opacity: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 20px;
  transition: opacity 0.3s ease;
  
  ${TeamMemberCard}:hover & {
    opacity: 1;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #105090;
  font-size: 16px;
  transition: all 0.3s ease;
  transform: translateY(20px);
  opacity: 0;
  
  ${TeamMemberCard}:hover & {
    transform: translateY(0);
    opacity: 1;
  }
  
  &:hover {
    background: white;
    transform: translateY(-5px);
  }
  
  &:nth-child(1) {
    transition-delay: 0.1s;
  }
  
  &:nth-child(2) {
    transition-delay: 0.2s;
  }
  
  &:nth-child(3) {
    transition-delay: 0.3s;
  }
`;

const MemberDetails = styled.div`
  padding: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 40px;
    background-color: ${props => props.color};
  }
`;

const MemberName = styled.h4`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const MemberTitle = styled.p`
  font-size: 15px;
  color: #666;
  margin-bottom: 15px;
  font-weight: 500;
`;

const MemberBio = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #777;
  flex: 1;
`;

// Join Team section
const JoinTeam = styled.div`
  margin-top: 80px;
  padding: 40px;
  background: linear-gradient(135deg, #105090, #2090B0);
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
  animation: ${fadeIn} 0.4s ease-out;
  
  @media (max-width: 768px) {
    padding: 30px;
    margin-top: 60px;
  }
  
  @media (max-width: 576px) {
    padding: 25px;
    margin-top: 40px;
  }
`;

const JoinTeamContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const JoinTeamTitle = styled.h3`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 20px;
  color: white;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const JoinTeamDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 30px;
  max-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 25px;
  }
`;

const JoinTeamButton = styled.button`
  background: white;
  color: #105090;
  font-weight: 600;
  padding: 14px 30px;
  border-radius: 50px;
  border: none;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
    background: #f0f0f0;
  }
  
  @media (max-width: 768px) {
    padding: 12px 26px;
    font-size: 14px;
  }
`;
